import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, RotateCcw, Paperclip, Eye, Search, Trash2 } from 'lucide-react';
import { PageLoader, StatusBadge, CategoryBadge, StatCard, Modal, EmailPreview, fmtDateTime, Spinner, ConfirmDialog } from '../../components/ui';
import api, { errMsg, downloadFile } from '../../services/api';

const VIEWS = [
  ['', 'All'], ['opened', 'Opened'], ['notOpened', 'Not opened'], ['FAILED', 'Failed'], ['QUEUED', 'Queued'],
];

export default function NotificationReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [n, setN] = useState(null);
  const [rows, setRows] = useState([]);
  const [view, setView] = useState('');
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/notifications/${id}`);
    if (data.status === 'DRAFT') return navigate(`/admin/notifications/${id}/edit`, { replace: true });
    setN(data);
    const params = {};
    if (view === 'opened') params.opened = 'true';
    else if (view === 'notOpened') { params.opened = 'false'; params.status = 'SENT'; }
    else if (view) params.status = view;
    if (search) params.search = search;
    const r = await api.get(`/admin/notifications/${id}/recipients`, { params });
    setRows(r.data);
  }, [id, view, search, navigate]);

  useEffect(() => { const t = setTimeout(load, search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  // Refresh while sending
  useEffect(() => {
    if (n?.status !== 'SENDING') return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [n?.status, load]);

  const retry = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post(`/admin/notifications/${id}/retry`);
      toast.success(`Retrying ${data.count} email(s)`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setRetrying(false);
    }
  };

  const showPreview = async () => {
    const { data } = await api.get(`/admin/notifications/${id}/preview`);
    setPreview(data);
  };

  if (!n) return <PageLoader />;
  const openRate = n.sentCount ? Math.round((n.openedCount / n.sentCount) * 100) : 0;
  const progress = n.totalCount ? Math.round(((n.sentCount + n.failedCount) / n.totalCount) * 100) : 0;

  return (
    <div>
      <Link to="/admin/notifications" className="mb-3 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-navy-800"><ArrowLeft size={14} />Notifications</Link>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900">{n.subject}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <StatusBadge status={n.status} /><CategoryBadge category={n.category} />
            <span>{n.layout?.name}</span><span>· sent {fmtDateTime(n.sentAt)}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={showPreview} className="btn-outline"><Eye size={16} />View email</button>
          {n.attachmentName && <button onClick={() => downloadFile(`/admin/notifications/${id}/attachment`, n.attachmentName)} className="btn-outline"><Paperclip size={16} />{n.attachmentName}</button>}
          {n.failedCount > 0 && n.status !== 'SENDING' && <button onClick={retry} disabled={retrying} className="btn-primary">{retrying ? <Spinner size={16} /> : <RotateCcw size={16} />}Retry failed</button>}
          {n.status !== 'SENDING' && <button onClick={() => setConfirmDelete(true)} className="btn-ghost text-red-600"><Trash2 size={16} /></button>}
        </div>
      </div>

      {n.status === 'SENDING' && (
        <div className="card mb-5 p-4">
          <div className="mb-2 flex justify-between text-sm"><span className="flex items-center gap-2 text-navy-800"><Spinner size={14} />Sending…</span><span>{progress}%</span></div>
          <div className="h-2 overflow-hidden rounded-full bg-gray-100"><div className="h-full bg-gold-500 transition-all" style={{ width: `${progress}%` }} /></div>
        </div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Recipients" value={n.totalCount} />
        <StatCard label="Delivered" value={n.sentCount} tone="green" />
        <StatCard label="Failed" value={n.failedCount} tone={n.failedCount ? 'red' : 'gray'} />
        <StatCard label="Opened" value={n.openedCount} tone="gold" hint={`${openRate}% open rate`} />
      </div>

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 border-b p-4">
          {VIEWS.map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} className={`rounded-full px-3 py-1 text-sm ${view === v ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{l}</button>
          ))}
          <div className="relative ml-auto w-full sm:w-60">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className="input py-1.5 pl-9" placeholder="Search recipients" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="max-h-[560px] overflow-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-gray-50"><tr><th className="th">Member</th><th className="th">Status</th><th className="th">Sent</th><th className="th">Opened</th></tr></thead>
            <tbody className="divide-y">
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="td"><p className="font-medium text-navy-900">{r.name}</p><p className="text-xs text-gray-500">{r.email}</p></td>
                  <td className="td"><StatusBadge status={r.status} />{r.error && <p className="mt-1 max-w-xs truncate text-xs text-red-600" title={r.error}>{r.error}</p>}</td>
                  <td className="td whitespace-nowrap text-gray-500">{fmtDateTime(r.sentAt)}</td>
                  <td className="td whitespace-nowrap">{r.openedAt ? <span className="text-green-700">{fmtDateTime(r.openedAt)}{r.openCount > 1 && <span className="text-xs text-gray-400"> ({r.openCount}×)</span>}</span> : <span className="text-gray-400">—</span>}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={4} className="td py-10 text-center text-gray-400">No recipients in this view</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="border-t px-4 py-3 text-xs text-gray-400">Opens are counted when the member's email app loads images, so the real open rate is usually a little higher.</p>
      </div>

      {preview && (
        <Modal title="Email" wide onClose={() => setPreview(null)}>
          <p className="mb-3 text-sm"><span className="text-gray-500">Subject:</span> <strong>{preview.subject}</strong></p>
          <EmailPreview html={preview.html} />
        </Modal>
      )}
      {confirmDelete && (
        <ConfirmDialog title="Delete notification?" danger confirmLabel="Delete" message="This removes the notification and its delivery report. Emails already delivered are not affected."
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => { await api.delete(`/admin/notifications/${id}`); toast.success('Deleted'); navigate('/admin/notifications'); }} />
      )}
    </div>
  );
}
